import { useEffect, useRef, useState } from 'react';
import type { State } from '../shared/model';
import { stageLabels } from '../shared/model';
import type { ReconciliationPreview, ReconciliationWorkspaceData } from '../shared/reconciliation';
import './reconciliation.css';

export function ReconciliationWorkspace({
  data,
  onChanged,
  openContent,
}: {
  data: State;
  onChanged: () => void | Promise<void>;
  openContent?: (id: string) => void;
}) {
  const [workspace, setWorkspace] = useState<ReconciliationWorkspaceData>();
  const [operationalId, setOperationalId] = useState('');
  const [importedId, setImportedId] = useState('');
  const [preview, setPreview] = useState<ReconciliationPreview>();
  const [choice, setChoice] = useState<'' | 'operational' | 'archive'>('');
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const alive = useRef(true);
  async function load() {
    const response = await fetch('/api/reconciliation');
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível consultar a conciliação.');
    if (alive.current) setWorkspace(result);
  }
  useEffect(() => {
    alive.current = true;
    void load().catch((failure) => {
      if (alive.current) setError(failure.message);
    });
    return () => {
      alive.current = false;
    };
  }, []);
  function resetPreview() {
    setPreview(undefined);
    setChoice('');
    setReason('');
    setChecked(false);
    setError('');
    setNotice('');
  }
  async function post(path: string, body: unknown) {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível concluir a conciliação.');
    return result;
  }
  async function inspect() {
    if (busy) return;
    resetPreview();
    setBusy(true);
    try {
      const result: ReconciliationPreview = await post('/api/reconciliation/preview', {
        operationalId,
        importedId,
      });
      if (alive.current) {
        setPreview(result);
        setChoice(result.operational.stage === result.imported.stage ? 'operational' : '');
      }
    } catch (failure) {
      if (alive.current) setError((failure as Error).message);
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  async function confirm() {
    if (
      busy ||
      !preview ||
      !choice ||
      !checked ||
      reason.trim().length < 20 ||
      preview.blockers.length
    )
      return;
    setBusy(true);
    setError('');
    try {
      const result = await post('/api/reconciliation/confirm', {
        operationalId: preview.operational.id,
        importedId: preview.imported.id,
        operationalVersion: preview.operational.version,
        importedVersion: preview.imported.version,
        sourceHash: preview.source.hash,
        previewHash: preview.previewHash,
        stageChoice: choice,
        reason,
      });
      if (alive.current) {
        resetPreview();
        setOperationalId('');
        setImportedId('');
        setNotice(
          `Ficha ${result.reconciliation.flgId} conciliada. A peça mantém seu ID interno e seu histórico operacional.`,
        );
      }
      await load();
      await onChanged();
    } catch (failure) {
      if (alive.current) {
        setError((failure as Error).message);
        setPreview(undefined);
        setChecked(false);
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  const effects = choice && preview ? preview.effects[choice] : undefined;
  return (
    <section className="reconciliation-workspace panel" aria-labelledby="reconciliation-heading">
      <h2 id="reconciliation-heading">Conciliar uma peça com sua ficha</h2>
      <p>
        Use quando uma peça criada no painel ganhou uma ficha oficial no acervo. Confira as duas
        identidades e o impacto antes de uni-las.
      </p>
      {error && (
        <p role="alert" className="notice error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="notice">
          {notice}
        </p>
      )}
      {!workspace ? (
        !error && <p>Carregando peças e conciliações…</p>
      ) : (
        <>
          {data.permissions.admin ? (
            <>
              <div className="reconciliation-selectors">
                <label className="field">
                  <span>Peça operacional sem ficha</span>
                  <select
                    value={operationalId}
                    disabled={busy}
                    onChange={(event) => {
                      setOperationalId(event.target.value);
                      resetPreview();
                    }}
                  >
                    <option value="">Selecione a peça criada no painel</option>
                    {workspace.operational.map((content) => (
                      <option key={content.id} value={content.id}>
                        {content.title} · {content.format} · {content.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Ficha oficial importada</span>
                  <select
                    value={importedId}
                    disabled={busy}
                    onChange={(event) => {
                      setImportedId(event.target.value);
                      resetPreview();
                    }}
                  >
                    <option value="">Selecione a ficha da mesma peça</option>
                    {workspace.imported.map((content) => (
                      <option key={content.id} value={content.id}>
                        {content.flgId} · {content.title} · {content.format}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="card-actions">
                <button
                  disabled={busy || !operationalId || !importedId}
                  onClick={() => void inspect()}
                >
                  {busy ? 'Conferindo…' : 'Conferir conciliação'}
                </button>
                <button
                  disabled={busy}
                  onClick={() => {
                    resetPreview();
                    void load().catch((failure) => setError(failure.message));
                  }}
                >
                  Atualizar peças
                </button>
              </div>
              {!workspace.operational.length && (
                <p className="fine-print">
                  Todas as peças atuais já têm uma referência de origem. Novas pautas sem ficha
                  aparecerão aqui.
                </p>
              )}
              {preview && (
                <div className="reconciliation-preview">
                  <h3>Prévia da conciliação</h3>
                  <div className="reconciliation-comparison">
                    <article>
                      <h4>Peça operacional</h4>
                      <p>{preview.operational.title}</p>
                      <p>
                        {stageLabels[preview.operational.stage]} · versão{' '}
                        {preview.operational.version}
                      </p>
                      <p className="fine-print">ID interno preservado: {preview.operational.id}</p>
                    </article>
                    <article>
                      <h4>Ficha oficial · {preview.imported.flgId}</h4>
                      <p>{preview.imported.title}</p>
                      <p>
                        {stageLabels[preview.imported.stage]} · versão {preview.imported.version}
                      </p>
                      <p className="fine-print">{preview.source.path}</p>
                    </article>
                  </div>
                  <p>
                    O título e o ID FLG passam a ser os da ficha. Responsável, notas, briefing,
                    arquivos e registros da peça operacional são preservados. Os vínculos de
                    campanha serão reunidos.
                  </p>
                  <details>
                    <summary>Referência importada e mapa de vínculos</summary>
                    <p className="fine-print">SHA-256 da ficha: {preview.source.hash}</p>
                    <p className="fine-print">
                      Commit de origem: {preview.source.commit || 'Não informado na importação'}
                    </p>
                    <p className="fine-print">
                      {preview.imported.id} → {preview.operational.id}
                    </p>
                    <div className="reconciliation-reference-list">
                      {preview.references
                        .filter(
                          (reference) =>
                            reference.operationalIds.length || reference.importedIds.length,
                        )
                        .map((reference) => (
                          <div key={reference.kind}>
                            <strong>{reference.label}</strong>
                            <span>
                              {reference.operationalIds.length} na peça ·{' '}
                              {reference.importedIds.length} na ficha
                            </span>
                            <small>
                              {reference.action === 'read_alias'
                                ? 'Os calendários armazenados são preservados; a leitura usará o mapa de identidade.'
                                : reference.importedIds.length
                                  ? 'A ficha já possui operação: conciliação bloqueada.'
                                  : 'IDs e registros operacionais preservados.'}
                            </small>
                          </div>
                        ))}
                    </div>
                  </details>
                  {!!preview.blockers.length && (
                    <div className="notice error">
                      <strong>A conciliação precisa de revisão</strong>
                      <ul>
                        {preview.blockers.map((blocker) => (
                          <li key={blocker}>{blocker}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {preview.operational.stage !== preview.imported.stage && (
                    <fieldset className="reconciliation-stage">
                      <legend>Qual etapa deve permanecer?</legend>
                      <label>
                        <input
                          type="radio"
                          name="reconciliation-stage"
                          value="operational"
                          checked={choice === 'operational'}
                          disabled={busy}
                          onChange={() => setChoice('operational')}
                        />{' '}
                        Manter etapa operacional: {stageLabels[preview.operational.stage]}
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="reconciliation-stage"
                          value="archive"
                          checked={choice === 'archive'}
                          disabled={busy || !preview.archiveStageAllowed}
                          onChange={() => setChoice('archive')}
                        />{' '}
                        Usar etapa do acervo: {stageLabels[preview.imported.stage]}
                      </label>
                      {!preview.archiveStageAllowed && (
                        <p className="fine-print">
                          Uma publicação comprovada mantém sua etapa operacional.
                        </p>
                      )}
                    </fieldset>
                  )}
                  {effects && (
                    <p className="notice">
                      Impacto da decisão: {effects.supersededPackageIds.length} pacote(s) precisarão
                      de nova revisão e aprovação; {effects.proposalUpdates.length} proposta(s)
                      terão a situação atualizada conforme o calendário importado.
                    </p>
                  )}
                  <label className="field">
                    <span>Motivo e evidência da conciliação</span>
                    <textarea
                      value={reason}
                      disabled={busy}
                      minLength={20}
                      maxLength={3000}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="Explique como conferiu que a pauta e a ficha representam a mesma peça."
                    />
                  </label>
                  <label className="reconciliation-confirmation">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={busy}
                      onChange={(event) => setChecked(event.target.checked)}
                    />{' '}
                    Conferi que as duas identidades são a mesma peça e revisei o impacto.
                  </label>
                  <button
                    className="primary"
                    disabled={
                      busy ||
                      !choice ||
                      !checked ||
                      reason.trim().length < 20 ||
                      !!preview.blockers.length
                    }
                    onClick={() => void confirm()}
                  >
                    Confirmar conciliação
                  </button>
                  <p className="fine-print">
                    A confirmação preserva as duas identidades anteriores no histórico. Os arquivos
                    Markdown não são alterados; o mapa evita recriar a duplicata na próxima
                    importação.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="notice">
              Um administrador pode preparar e confirmar a conciliação. O histórico permanece
              disponível para consulta.
            </p>
          )}
          <div className="reconciliation-history">
            <h3>Conciliações registradas</h3>
            {!workspace.history.length ? (
              <p>Nenhuma peça foi conciliada ainda.</p>
            ) : (
              workspace.history.map((record) => (
                <article key={record.id}>
                  <h4>
                    {record.flgId} · {record.canonicalAfter.title}
                  </h4>
                  <p>{record.reason}</p>
                  <p className="fine-print">
                    {new Date(record.createdAt).toLocaleString('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                    })}{' '}
                    · {record.actor}
                  </p>
                  <details>
                    <summary>Identidades e referência preservadas</summary>
                    <p className="fine-print">
                      {record.sourceId} → {record.canonicalId}
                    </p>
                    <p className="fine-print">
                      Ficha: {record.source.path}
                      <br />
                      SHA-256: {record.source.hash}
                    </p>
                  </details>
                  {openContent && (
                    <button onClick={() => openContent(record.canonicalId)}>
                      Abrir peça conciliada
                    </button>
                  )}
                </article>
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
}
